import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FiPackage, FiCheck, FiAlertCircle, FiX, FiPlus } from 'react-icons/fi';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import StatCard from '../components/StatCard';
import LoadingSpinner from '../components/LoadingSpinner';
import MapView from '../components/MapView';
import { immobilisationsAPI } from '../services/api';
import { toast } from 'react-toastify';
import ExportPDFButton from '../components/ExportPDFButton';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const [statsRes, mapRes] = await Promise.all([
          immobilisationsAPI.getStats(),
          immobilisationsAPI.getMapData().catch(() => ({ data: { data: [] } })),
        ]);

        // Backend retourne { statistics: { total, enrolled, byCategory, byEtat, byService, recentAdditions } }
        setStats(statsRes.data.statistics);
        setMapData(mapRes.data.data || []);
      } catch (error) {
        toast.error('Erreur lors du chargement du tableau de bord');
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Extraire les compteurs par état depuis le tableau byEtat
  const getEtatCount = (etat) => {
    if (!stats?.byEtat) return 0;
    const found = stats.byEtat.find(e => e.etat === etat);
    return found ? parseInt(found.count) : 0;
  };

  const getCategoryData = () => {
    if (!stats?.byCategory) return [];
    return stats.byCategory.map(cat => ({
      name: cat.nom || 'Sans catégorie',
      value: parseInt(cat.count)
    }));
  };

  const getStateData = () => {
    if (!stats?.byEtat) return [];
    const labels = { bon: 'Bon', moyen: 'Moyen', hors_service: 'Hors service' };
    return stats.byEtat.map(s => ({
      name: labels[s.etat] || s.etat,
      value: parseInt(s.count)
    }));
  };

  const COLORS = ['#10B981', '#C9A84C', '#EF4444', '#1A2B4A', '#0A9B9B', '#9B7B2E'];

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content" id="page-content">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="page-content" id="page-content">
          <div className="page-header">
            <h1 className="page-title">Tableau de Bord</h1>
            <ExportPDFButton title="OGP IMMO - Tableau de bord" fileName="dashboard-ogp-immo" />
            <Link to="/immobilisations/new" className="btn btn-secondary">
              <FiPlus /> Nouvelle immobilisation
            </Link>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-4">
            <StatCard
              icon={FiPackage}
              label="Total Immobilisations"
              value={stats?.total || 0}
              color="primary"
            />
            <StatCard
              icon={FiCheck}
              label="Bon État"
              value={getEtatCount('bon')}
              color="success"
            />
            <StatCard
              icon={FiAlertCircle}
              label="État Moyen"
              value={getEtatCount('moyen')}
              color="warning"
            />
            <StatCard
              icon={FiX}
              label="Hors Service"
              value={getEtatCount('hors_service')}
              color="error"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-2" style={{ marginTop: '2rem' }}>
            <div className="chart-container">
              <h3 className="chart-title">Immobilisations par Catégorie</h3>
              {getCategoryData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getCategoryData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1A2B4A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  Aucune donnée disponible
                </p>
              )}
            </div>

            <div className="chart-container">
              <h3 className="chart-title">Distribution par État</h3>
              {getStateData().length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getStateData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getStateData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  Aucune donnée disponible
                </p>
              )}
            </div>
          </div>

          {/* Map */}
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Localisation des Immobilisations</h3>
            <MapView immobilisations={mapData} />
          </div>

          {/* Recent Additions */}
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Derniers Ajouts</h3>
            <div className="card">
              <table>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>N° Identification</th>
                    <th>Date d'ajout</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recentAdditions && stats.recentAdditions.length > 0 ? (
                    stats.recentAdditions.slice(0, 5).map((immo) => (
                      <tr key={immo.id}>
                        <td>
                          <Link to={`/immobilisations/${immo.id}`} style={{ textDecoration: 'underline', color: 'var(--primary-color)' }}>
                            {immo.nom}
                          </Link>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {immo.numero_identification}
                        </td>
                        <td>{new Date(immo.created_at).toLocaleDateString('fr-FR')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', padding: '2rem' }}>
                        Aucune immobilisation récente
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
